import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BookOpen,
  ArrowLeft,
  UploadCloud,
  Loader2,
  Save,
  Plus,
  Trash2,
} from "lucide-react";
import {
  useAdminMagazine,
  useAdminMagazineRoles,
  useCreateMagazineMutation,
  useUpdateMagazineMutation,
} from "./useMagazineQueries";
import { adminMagazineAPI } from "./magazine.api";
import { toast } from "react-toastify";

const EMPTY_FORM = {
  title: "",
  slug: "",
  description: "",
  width: 453,
  height: 640,
  is_published: true,
  cover_image: "",
  access_type: "FREE",
  allowed_groups: "",
  preview_pages_count: 0,
  gate_title: "",
  gate_description: "",
  gate_button_text: "",
  gate_redirect_url: "",
  require_email_otp: false,
  pricing_type: "FREE",
  price: 0,
  currency: "INR",
  access_passcode: "",
  allow_screenshot: false,
  pdf_download_enabled: false,
  pdf_password_type: "USER_EMAIL",
  custom_pdf_password: "",
  access_duration_days: 0,
  lead_access_duration_days: 0,
  require_admin_approval: false,
};

const resolveCoverUrl = (urlOrKey) => {
  if (!urlOrKey) return "";
  if (
    urlOrKey.startsWith("http://") ||
    urlOrKey.startsWith("https://") ||
    urlOrKey.startsWith("blob:") ||
    urlOrKey.startsWith("data:")
  ) {
    return urlOrKey;
  }
  const cleanKey = urlOrKey.replace(/^\/+/, "");
  return `https://d23ksuhb21eg5b.cloudfront.net/${cleanKey}`;
};

export default function AdminMagazineForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { data: rawMagazine, isLoading: loadingExisting } = useAdminMagazine(
    isEdit ? Number(id) : null,
    { enabled: isEdit }
  );

  const { data: rolesData, isLoading: loadingRoles } = useAdminMagazineRoles();
  const systemRoles = Array.isArray(rolesData) ? rolesData : [];

  const existingMagazine = rawMagazine?.data || rawMagazine;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState("");
  const [coverLoadError, setCoverLoadError] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const createMutation = useCreateMagazineMutation();
  const updateMutation = useUpdateMagazineMutation();

  // Selected roles array derived from comma-separated string
  const currentAllowedRoles = (formData.allowed_groups || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const isAllMembers = currentAllowedRoles.some(
    (r) => r.toLowerCase() === "all_members"
  );

  const handleToggleAllMembers = () => {
    if (isAllMembers) {
      setFormData((prev) => ({ ...prev, allowed_groups: "" }));
    } else {
      setFormData((prev) => ({ ...prev, allowed_groups: "all_members" }));
    }
  };

  const handleToggleRole = (roleName) => {
    let current = currentAllowedRoles.filter(
      (r) => r.toLowerCase() !== "all_members"
    );
    const exists = current.some(
      (r) => r.toLowerCase() === roleName.toLowerCase()
    );
    if (exists) {
      current = current.filter(
        (r) => r.toLowerCase() !== roleName.toLowerCase()
      );
    } else {
      current.push(roleName);
    }
    setFormData((prev) => ({
      ...prev,
      allowed_groups: current.join(", "),
    }));
  };

  const handleSelectAllRoles = () => {
    const allRoleNames = systemRoles.map((r) => r.name || r.role_name);
    setFormData((prev) => ({
      ...prev,
      allowed_groups: allRoleNames.join(", "),
    }));
  };

  const handleClearAllRoles = () => {
    setFormData((prev) => ({
      ...prev,
      allowed_groups: "",
    }));
  };

  // Populate form when editing an existing magazine
  useEffect(() => {
    if (isEdit && existingMagazine && existingMagazine.title !== undefined) {
      setFormData({
        title: existingMagazine.title || "",
        slug: existingMagazine.slug || "",
        description: existingMagazine.description || "",
        width: existingMagazine.width || 453,
        height: existingMagazine.height || 640,
        is_published: existingMagazine.is_published ?? true,
        cover_image: existingMagazine.cover_image || "",
        access_type: existingMagazine.access_type || "FREE",
        allowed_groups:
          existingMagazine.allowed_groups ||
          (Array.isArray(existingMagazine.allowed_roles)
            ? existingMagazine.allowed_roles.join(", ")
            : ""),
        preview_pages_count: existingMagazine.preview_pages_count || 0,
        gate_title: existingMagazine.gate_title || "",
        gate_description: existingMagazine.gate_description || "",
        gate_button_text: existingMagazine.gate_button_text || "",
        gate_redirect_url: existingMagazine.gate_redirect_url || "",
        require_email_otp: existingMagazine.require_email_otp ?? false,
        pricing_type: existingMagazine.pricing_type || "FREE",
        price: existingMagazine.price || 0,
        currency: existingMagazine.currency || "INR",
        access_passcode: existingMagazine.access_passcode || "",
        allow_screenshot: existingMagazine.allow_screenshot ?? false,
        pdf_download_enabled: existingMagazine.pdf_download_enabled ?? false,
        pdf_password_type: existingMagazine.pdf_password_type || "USER_EMAIL",
        custom_pdf_password: existingMagazine.custom_pdf_password || "",
        access_duration_days: existingMagazine.access_duration_days || 0,
        lead_access_duration_days: existingMagazine.lead_access_duration_days || 0,
        require_admin_approval: existingMagazine.require_admin_approval ?? false,
      });
      if (existingMagazine.cover_image) {
        setCoverPreviewUrl(existingMagazine.cover_image);
        setCoverLoadError(false);
      }
    }
  }, [isEdit, existingMagazine]);

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title: val,
      slug:
        !isEdit && !prev.slug
          ? val
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, "")
          : prev.slug,
    }));
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Immediately display chosen local image so preview is never blank
    const localUrl = URL.createObjectURL(file);
    setCoverPreviewUrl(localUrl);
    setCoverLoadError(false);

    setUploadingCover(true);
    try {
      const ext = file.name.split(".").pop() || "webp";
      const filename = `cover_${Date.now()}.${ext}`;

      const { presigned_url, object_key } =
        await adminMagazineAPI.getPresignedCoverUploadUrl({
          filename,
          content_type: file.type || "image/webp",
          magazine_id: isEdit ? Number(id) : 0,
        });

      await adminMagazineAPI.uploadToS3(
        presigned_url,
        file,
        file.type || "image/webp"
      );
      setFormData((prev) => ({ ...prev, cover_image: object_key }));
      toast.success("Cover image uploaded successfully!");
    } catch (err) {
      console.error("Cover upload failed:", err);
      toast.error(err.response?.data?.detail || "Cover upload failed.");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCoverTextChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, cover_image: val }));
    setCoverPreviewUrl(val);
    setCoverLoadError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim() || !formData.slug?.trim()) {
      toast.error("Title and Slug are required.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim(),
        slug: formData.slug.trim(),
        description: formData.description || null,
        width: Number(formData.width) || 453,
        height: Number(formData.height) || 640,
        is_published: Boolean(formData.is_published),
        cover_image: formData.cover_image || null,
        access_type: formData.access_type,
        allowed_groups: formData.allowed_groups || null,
        preview_pages_count: Number(formData.preview_pages_count) || 0,
        gate_title: formData.gate_title || null,
        gate_description: formData.gate_description || null,
        gate_button_text: formData.gate_button_text || null,
        gate_redirect_url: formData.gate_redirect_url || null,
        require_email_otp: Boolean(formData.require_email_otp),
        pricing_type: formData.pricing_type,
        price: Number(formData.price) || 0,
        currency: formData.currency || "INR",
        access_passcode: formData.access_passcode || null,
        allow_screenshot: Boolean(formData.allow_screenshot),
        pdf_download_enabled: Boolean(formData.pdf_download_enabled),
        pdf_password_type: formData.pdf_password_type,
        custom_pdf_password: formData.custom_pdf_password || null,
        access_duration_days: Number(formData.access_duration_days) || 0,
        lead_access_duration_days: Number(formData.lead_access_duration_days) || 0,
        require_admin_approval: Boolean(formData.require_admin_approval),
      };

      if (isEdit) {
        await updateMutation.mutateAsync({ id: Number(id), payload });
        toast.success("Edition updated successfully.");
      } else {
        await createMutation.mutateAsync(payload);
        toast.success("Edition created successfully.");
      }
      navigate("/admin/magazines");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save edition.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && loadingExisting) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        <span className="text-sm font-medium text-slate-600">Loading edition...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6 bg-slate-50/50 min-h-screen">
      {/* PAGE HEADER */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/admin/magazines")}
          className="p-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer shadow-2xs"
          title="Back to Magazines"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600 shrink-0">
          <BookOpen className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 leading-tight">
            {isEdit ? "Edit Publication Edition" : "New Publication Edition"}
          </h1>
          <p className="text-xs text-slate-500">
            {isEdit
              ? "Update the edition metadata, access policies and DRM settings."
              : "Configure the new magazine edition before uploading pages."}
          </p>
        </div>
      </div>

      {!isEdit && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/80 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-600 text-white flex items-center justify-center shrink-0 shadow-xs">
              <UploadCloud className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-orange-950">Have a PDF ready?</h3>
              <p className="text-[11px] text-orange-800/80">
                Import directly from PDF — canvas dimensions, pages, and cover are auto-generated.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/magazines?import=true")}
            className="px-3.5 py-1.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs shrink-0 shadow-2xs cursor-pointer transition-all self-start sm:self-auto"
          >
            Import from PDF
          </button>
        </div>
      )}

      {/* FORM */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-sm border border-slate-200 divide-y divide-slate-100 text-xs text-slate-800"
      >
        {/* Section 1: Publication Identity */}
        <div className="p-6 space-y-4">
          <div className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">
            1. Publication Identity
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="e.g. HR Leadership Digest Q1"
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                URL Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="e.g. hr-leadership-digest-q1"
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 placeholder-slate-400 font-mono focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
          </div>

          <div>
            <label className="block font-medium mb-1.5 text-slate-700">
              Description
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief summary of this edition..."
              className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 placeholder-slate-400 resize-none focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
            />
          </div>

          {/* Cover Image */}
          <div>
            <label className="block font-medium mb-1.5 text-slate-700">
              Cover Image
            </label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={formData.cover_image}
                onChange={handleCoverTextChange}
                placeholder="S3 Key or Image URL (e.g. magazines/0/cover/cover.png)"
                className="flex-1 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
              <label className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 font-semibold cursor-pointer shrink-0 flex items-center gap-1.5 transition-colors shadow-2xs">
                {uploadingCover ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
                ) : (
                  <UploadCloud className="w-3.5 h-3.5" />
                )}
                <span>{uploadingCover ? "Uploading..." : "Upload Cover"}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  className="hidden"
                  disabled={uploadingCover}
                />
              </label>
            </div>

            {/* Cover Preview Card */}
            {(coverPreviewUrl || formData.cover_image) && (
              <div className="mt-3 flex items-center gap-4 p-3 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="relative w-20 h-28 rounded-xl overflow-hidden border border-slate-200 bg-white flex items-center justify-center shrink-0 shadow-2xs group">
                  {!coverLoadError && (coverPreviewUrl || formData.cover_image) ? (
                    <img
                      src={resolveCoverUrl(coverPreviewUrl || formData.cover_image)}
                      alt="Cover preview"
                      className="w-full h-full object-cover"
                      onLoad={() => setCoverLoadError(false)}
                      onError={() => setCoverLoadError(true)}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-slate-400 p-2 text-center">
                      <BookOpen className="w-5 h-5 mb-1 text-slate-300" />
                      <span className="text-[9px] font-mono leading-tight">Cover</span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, cover_image: "" }));
                      setCoverPreviewUrl("");
                      setCoverLoadError(false);
                    }}
                    className="absolute top-1 right-1 p-1 bg-white/95 hover:bg-red-50 text-slate-500 hover:text-red-600 rounded-lg shadow-sm border border-slate-200 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                    title="Remove Cover"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>

                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800">Cover Attached</span>
                    {uploadingCover && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-orange-600 font-medium">
                        <Loader2 className="w-3 h-3 animate-spin" /> Uploading to S3...
                      </span>
                    )}
                  </div>
                  <p
                    className="text-[11px] text-slate-600 font-mono truncate bg-white px-2 py-1 rounded-md border border-slate-200 select-all"
                    title={formData.cover_image}
                  >
                    {formData.cover_image || "Local preview (uploading...)"}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Ratio {formData.width} × {formData.height} px
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                Page Width (px)
              </label>
              <input
                type="number"
                min={100}
                value={formData.width}
                onChange={(e) => setFormData({ ...formData, width: Number(e.target.value) })}
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                Page Height (px)
              </label>
              <input
                type="number"
                min={100}
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: Number(e.target.value) })}
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Access & Gating Policies */}
        <div className="p-6 space-y-4">
          <div className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">
            2. Access &amp; Gating Policies
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                Access Mode
              </label>
              <select
                value={formData.access_type}
                onChange={(e) => setFormData({ ...formData, access_type: e.target.value })}
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs cursor-pointer"
              >
                <option value="FREE">Free (Public Reading)</option>
                <option value="EMAIL_LEAD">Email Lead Capture</option>
                <option value="MEMBER_ONLY">Member Exclusive</option>
                <option value="PASSCODE">Passcode Protected</option>
                <option value="PAID">Paid Edition</option>
              </select>
            </div>

            <div>
              <label className="block font-medium mb-1 text-slate-700">
                Preview Pages Count
              </label>
              <div className="text-[11px] text-slate-400 mb-1.5 leading-normal">
                Includes cover page. 0 = gated from cover. Set to <strong>3</strong> to preview Cover + first 2 pages.
              </div>
              <input
                type="number"
                min={0}
                value={formData.preview_pages_count}
                onChange={(e) =>
                  setFormData({ ...formData, preview_pages_count: Number(e.target.value) })
                }
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
          </div>

          {formData.access_type === "MEMBER_ONLY" && (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <label className="block font-bold text-slate-800">
                    Allowed Member Roles <span className="text-red-500">*</span>
                  </label>
                  <p className="text-[11px] text-slate-500">
                    Select which roles have access to read this edition.
                  </p>
                </div>
                <div className="flex items-center gap-1.5 text-[11px]">
                  <button
                    type="button"
                    onClick={handleSelectAllRoles}
                    className="px-2.5 py-1 rounded-lg bg-white hover:bg-slate-100 border border-slate-200 font-semibold text-slate-700 transition-colors cursor-pointer shadow-2xs"
                  >
                    Select All Roles
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllRoles}
                    className="px-2.5 py-1 rounded-lg bg-white hover:bg-slate-100 border border-slate-200 font-semibold text-slate-700 transition-colors cursor-pointer shadow-2xs"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* All Members Toggle */}
              <div
                onClick={handleToggleAllMembers}
                className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                  isAllMembers
                    ? "bg-orange-50 border-orange-300 text-orange-700 font-bold"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isAllMembers}
                  onChange={handleToggleAllMembers}
                  className="rounded text-orange-600 cursor-pointer"
                />
                <span className="text-xs">
                  Grant Access to <strong>All Active Members</strong> (all_members)
                </span>
              </div>

              {/* Individual Roles Badges / Buttons */}
              {loadingRoles ? (
                <div className="py-2 flex items-center gap-2 text-slate-400 text-xs">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
                  <span>Loading available roles...</span>
                </div>
              ) : systemRoles.length > 0 ? (
                <div>
                  <div className="text-[11px] font-semibold text-slate-600 mb-2">
                    Or select specific roles:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {systemRoles.map((role) => {
                      const roleName = role.name || role.role_name;
                      const isSelected =
                        !isAllMembers &&
                        currentAllowedRoles.some(
                          (r) => r.toLowerCase() === roleName.toLowerCase()
                        );
                      return (
                        <button
                          key={role.id || role.role_id || roleName}
                          type="button"
                          onClick={() => handleToggleRole(roleName)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all cursor-pointer ${
                            isSelected
                              ? "bg-orange-600 text-white border-orange-600 shadow-xs"
                              : isAllMembers
                              ? "bg-slate-100 text-slate-400 border-slate-200 opacity-60"
                              : "bg-white text-slate-700 border-slate-200 hover:border-orange-400"
                          }`}
                        >
                          <span>{roleName}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  Custom Groups / Comma-separated Role Strings:
                </label>
                <input
                  type="text"
                  value={formData.allowed_groups}
                  onChange={(e) =>
                    setFormData({ ...formData, allowed_groups: e.target.value })
                  }
                  placeholder="e.g. Educator, Spoc, Trainer, all_members"
                  className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 font-mono text-[11px] text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-2xs"
                />
              </div>
            </div>
          )}

          {(formData.access_type === "EMAIL_LEAD" || formData.require_admin_approval) && (
            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.require_email_otp}
                onChange={(e) =>
                  setFormData({ ...formData, require_email_otp: e.target.checked })
                }
                className="rounded text-orange-600 cursor-pointer"
              />
              <span className="font-medium">
                Require 6-digit email OTP verification before granting access
              </span>
            </label>
          )}

          {formData.access_type === "PASSCODE" && (
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                Edition Passcode
              </label>
              <input
                type="text"
                value={formData.access_passcode}
                onChange={(e) =>
                  setFormData({ ...formData, access_passcode: e.target.value })
                }
                placeholder="e.g. VIP2026"
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 font-mono focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
          )}

          {formData.access_type !== "FREE" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium mb-1.5 text-slate-700">
                  Gate Title
                </label>
                <input
                  type="text"
                  value={formData.gate_title}
                  onChange={(e) => setFormData({ ...formData, gate_title: e.target.value })}
                  placeholder="e.g. Unlock this Edition"
                  className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
                />
              </div>
              <div>
                <label className="block font-medium mb-1.5 text-slate-700">
                  Gate Button Text
                </label>
                <input
                  type="text"
                  value={formData.gate_button_text}
                  onChange={(e) =>
                    setFormData({ ...formData, gate_button_text: e.target.value })
                  }
                  placeholder="e.g. Get Access"
                  className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block font-medium mb-1.5 text-slate-700">
                  Gate Description
                </label>
                <textarea
                  rows={2}
                  value={formData.gate_description}
                  onChange={(e) =>
                    setFormData({ ...formData, gate_description: e.target.value })
                  }
                  placeholder="Explain why the user needs to unlock..."
                  className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 resize-none focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
                />
              </div>
            </div>
          )}

          {formData.access_type === "EMAIL_LEAD" && (
            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.require_admin_approval}
                onChange={(e) =>
                  setFormData({ ...formData, require_admin_approval: e.target.checked })
                }
                className="rounded text-orange-600 cursor-pointer"
              />
              <span className="font-medium">
                Require admin approval for access requests
              </span>
            </label>
          )}

          {formData.access_type === "MEMBER_ONLY" && (
            <div className="p-3.5 rounded-xl bg-blue-50/70 border border-blue-200 text-xs text-blue-800 leading-relaxed">
              <strong>Member Exclusive Policy:</strong> Access is strictly restricted to authenticated and approved EduSkills HR Community members with the allowed roles above. Non-members who attempt to read this edition will be guided to log in or apply.
            </div>
          )}
        </div>

        {/* Section 3: DRM & Retention */}
        <div className="p-6 space-y-4">
          <div className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">
            3. DRM &amp; Retention Configuration
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                Rental Access Duration{" "}
                <span className="font-normal text-slate-400">(days, 0 = lifetime)</span>
              </label>
              <input
                type="number"
                min={0}
                value={formData.access_duration_days}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    access_duration_days: Number(e.target.value),
                  })
                }
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
            <div>
              <label className="block font-medium mb-1.5 text-slate-700">
                Lead Access Duration{" "}
                <span className="font-normal text-slate-400">(days, 0 = lifetime)</span>
              </label>
              <input
                type="number"
                min={0}
                value={formData.lead_access_duration_days}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    lead_access_duration_days: Number(e.target.value),
                  })
                }
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allow_screenshot}
                onChange={(e) =>
                  setFormData({ ...formData, allow_screenshot: e.target.checked })
                }
                className="rounded text-orange-600 cursor-pointer"
              />
              <span className="font-medium">Allow Screenshots &amp; Screen Recording</span>
            </label>

            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.pdf_download_enabled}
                onChange={(e) =>
                  setFormData({ ...formData, pdf_download_enabled: e.target.checked })
                }
                className="rounded text-orange-600 cursor-pointer"
              />
              <span className="font-medium">Enable Certified PDF Download</span>
            </label>
          </div>
        </div>

        {/* Section 4: Publishing */}
        <div className="p-6 flex items-center justify-between">
          <div>
            <div className="font-bold text-slate-900">Publish Edition</div>
            <div className="text-slate-400 text-[11px] mt-0.5">
              Make this edition publicly visible in the reader catalog
            </div>
          </div>
          <input
            type="checkbox"
            checked={formData.is_published}
            onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
            className="w-4 h-4 rounded text-orange-600 cursor-pointer"
          />
        </div>

        {/* FOOTER ACTIONS */}
        <div className="p-4 flex items-center justify-end gap-3 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
          <button
            type="button"
            onClick={() => navigate("/admin/magazines")}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 font-semibold text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer shadow-2xs"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || uploadingCover}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold shadow-sm shadow-orange-600/20 transition-all disabled:opacity-50 cursor-pointer"
          >
            {submitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : isEdit ? (
              <Save className="w-3.5 h-3.5" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            <span>{isEdit ? "Save Changes" : "Create Edition"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
